import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Col,
  Empty,
  Grid,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import type { CoorteDeEntrada } from '@titularis/api-client';
import { veuDaCor } from '@titularis/ui';
import { api } from '../../api';
import { formatarTaxa } from './vocabulario';
import { proporcao, rotuloDoDia, somarSemanas } from './recorrencia';

/**
 * Quem volta a estudar, olhando a base inteira.
 *
 * **A lista responde "quem"; esta aba responde "se".** Ordenar a lista pela
 * recorrência mostra as pessoas que voltam, e não diz se isso é muito ou pouco -
 * nem se está melhorando. Aqui a pergunta é da base: de quem estudou, quantos
 * voltaram; de quem estudou na semana passada, quantos seguiram nesta; e, para
 * cada leva de contas, quantas continuam estudando semana após semana.
 *
 * **Por semana de entrada, e não só por semana do calendário.** "Doze estudaram
 * nesta semana" não diz se são os mesmos doze da passada ou doze novos que
 * chegaram pelo último post, e é essa a diferença entre um produto que segura
 * gente e um que só recebe. Cada linha é uma leva, e as colunas contam a partir
 * da chegada dela: a primeira coluna de todas as linhas é a mesma distância da
 * entrada, e é isso que deixa comparar a leva de uma campanha com a da seguinte.
 *
 * A população é a do funil - só alunos de verdade -, então o "estudaram" daqui
 * é o mesmo degrau lá de cima.
 */
export function RecorrenciaDasPessoas() {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();

  const recorrencia = useQuery({
    queryKey: ['recorrencia-das-pessoas'],
    queryFn: () => api.peopleRecurrence(),
  });

  if (recorrencia.error) {
    return <Alert type="error" showIcon title={(recorrencia.error as Error).message} />;
  }

  if (!recorrencia.data) {
    return (
      <Card size="small">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  const dados = recorrencia.data;

  /*
   * Tantas colunas quantas semanas a leva mais antiga já viveu, e não um número
   * fixo: quem decide a janela é a API, e com a base começando em setembro oito
   * colunas seriam cinco vazias.
   */
  const semanasAcompanhadas = Math.max(1, ...dados.coortes.map((coorte) => coorte.porSemana.length));

  /*
   * Três números, e cada um com o denominador escrito embaixo. Um "2" sozinho
   * não diz nada; "2 de 16 que estudaram" diz se é pouco.
   */
  const cartoes = [
    {
      titulo: 'Voltaram a estudar',
      valor: dados.voltaram,
      rodape:
        dados.estudaram > 0
          ? `de ${dados.estudaram} que estudaram · ${formatarTaxa(proporcao(dados.voltaram, dados.estudaram))}`
          : 'ninguém estudou ainda',
      ajuda:
        'Estudaram em dois dias diferentes ou mais, desde que entraram. A lista de Pessoas, filtrada por "Voltou a estudar", mostra quem são.',
    },
    {
      titulo: 'Estudaram nesta semana',
      valor: dados.nestaSemana,
      rodape: `desde ${rotuloDoDia(dados.semanaAtual)} · na passada, ${dados.naSemanaPassada}`,
      ajuda:
        'A semana em andamento, de segunda-feira até agora. Ela só se compara de igual para igual com a passada no domingo.',
    },
    {
      titulo: 'Seguiram da semana passada',
      valor: dados.seguiram,
      rodape:
        dados.naSemanaPassada > 0
          ? `de ${dados.naSemanaPassada} · ${formatarTaxa(proporcao(dados.seguiram, dados.naSemanaPassada))}`
          : 'ninguém estudou na semana passada',
      ajuda:
        'Estudaram na semana passada e voltaram a estudar nesta. É o sinal de hábito: a mesma gente, uma semana depois da outra.',
    },
  ];

  /** O véu da célula cresce com a proporção, com teto: o texto por cima tem de continuar legível. */
  const fundoDa = (taxa: number | null) =>
    taxa ? veuDaCor(token.colorPrimary, 0.08 + 0.4 * taxa) : undefined;

  const colunasDasSemanas: TableColumnsType<CoorteDeEntrada> = Array.from(
    { length: semanasAcompanhadas },
    (_, depois) => ({
      title: depois === 0 ? 'Entrada' : `+${depois}`,
      key: `semana-${depois}`,
      // "Entrada" mede 60px no cabeçalho; com 68, o celular a partia em "Entrad-a".
      width: 76,
      align: 'center' as const,
      onCell: (coorte: CoorteDeEntrada) => {
        const estudaram = coorte.porSemana[depois];
        return {
          style: {
            background:
              estudaram === undefined ? undefined : fundoDa(proporcao(estudaram, coorte.contas)),
          },
        };
      },
      render: (_: unknown, coorte: CoorteDeEntrada) => {
        const estudaram = coorte.porSemana[depois];
        // A semana ainda não chegou para esta leva: vazio, e não zero.
        if (estudaram === undefined) return null;

        const emAndamento = depois === coorte.porSemana.length - 1;
        const semana = rotuloDoDia(somarSemanas(coorte.inicio, depois));

        return (
          <Tooltip
            title={`${estudaram} de ${coorte.contas} estudaram na semana de ${semana}${
              emAndamento ? ', que ainda está em andamento' : ''
            }`}
          >
            <Typography.Text
              type={estudaram === 0 ? 'secondary' : undefined}
              italic={emAndamento}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatarTaxa(proporcao(estudaram, coorte.contas))}
            </Typography.Text>
          </Tooltip>
        );
      },
    }),
  );

  const comProporcao = (valor: number, total: number) => (
    <Space size={6}>
      <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {formatarTaxa(proporcao(valor, total))}
      </Typography.Text>
    </Space>
  );

  const colunas: TableColumnsType<CoorteDeEntrada> = [
    {
      title: 'Semana de entrada',
      key: 'inicio',
      width: 120,
      // Fixa no celular: é ela que dá sentido às colunas que rolam ao lado.
      fixed: 'start',
      render: (_: unknown, coorte) => (
        <Typography.Text strong>
          {rotuloDoDia(coorte.inicio)}
          {coorte.inicio === dados.semanaAtual ? (
            <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
              {' '}
              (esta)
            </Typography.Text>
          ) : null}
        </Typography.Text>
      ),
    },
    {
      title: 'Contas',
      key: 'contas',
      width: 80,
      align: 'right',
      render: (_: unknown, coorte) => coorte.contas,
    },
    {
      title: (
        <Tooltip title="Estudaram ao menos um dia, em qualquer semana até hoje.">
          Estudaram
        </Tooltip>
      ),
      key: 'estudaram',
      width: 110,
      /*
       * No celular saem as duas contagens de "até hoje", e o calor fica: é ele
       * que responde a pergunta da aba, e os dois totais já estão nos cartões
       * de cima. Com elas, a primeira semana começava fora da tela.
       */
      responsive: ['md'],
      /*
       * À direita, como as Contas ao lado: são três contagens da mesma leva, e
       * alinhadas por lados diferentes a linha de uma conta só se lia "1 1 100%".
       */
      align: 'right',
      render: (_: unknown, coorte) => comProporcao(coorte.estudaram, coorte.contas),
    },
    {
      title: (
        <Tooltip title="Estudaram em dois dias diferentes ou mais, até hoje.">Voltaram</Tooltip>
      ),
      key: 'voltaram',
      width: 110,
      responsive: ['md'],
      align: 'right',
      render: (_: unknown, coorte) => comProporcao(coorte.voltaram, coorte.contas),
    },
    {
      title: 'Estudaram na semana, contando da entrada',
      key: 'semanas',
      children: colunasDasSemanas,
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[token.margin, token.margin]}>
        {cartoes.map((cartao) => (
          <Col key={cartao.titulo} xs={24} sm={8}>
            {/* O balão no cartão inteiro, como nos degraus do funil logo acima. */}
            <Tooltip title={cartao.ajuda}>
              <Card size="small" style={{ height: '100%' }}>
                <Statistic title={cartao.titulo} value={cartao.valor} groupSeparator="." />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {cartao.rodape}
                </Typography.Text>
              </Card>
            </Tooltip>
          </Col>
        ))}
      </Row>

      <Card
        size="small"
        title="Por semana de entrada"
        styles={{ body: { padding: 0 } }}
      >
        <Typography.Paragraph
          type="secondary"
          style={{ margin: 0, padding: token.paddingSM, fontSize: 13 }}
        >
          Cada linha é a leva de contas de aluno criadas numa semana. As colunas da direita dizem
          quantas delas estudaram na própria semana de entrada e em cada semana seguinte; a última
          célula de cada linha, em itálico, é a semana em andamento, e ainda pode subir.
        </Typography.Paragraph>

        <Table<CoorteDeEntrada>
          size="small"
          rowKey="inicio"
          dataSource={dados.coortes}
          columns={colunas}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhuma conta de aluno entrou nas últimas oito semanas."
              />
            ),
          }}
          // A soma das larguras visíveis: fixa, como na lista, para não crescer com o texto.
          scroll={{ x: 120 + 80 + (telas.md ? 110 + 110 : 0) + 76 * semanasAcompanhadas }}
        />
      </Card>
    </Space>
  );
}
